import { trpc } from "@/lib/trpc";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";

type Role = "admin" | "user";

export function InviteDialog({
	currentUserId,
	onClose,
}: {
	currentUserId: string | undefined;
	onClose: () => void;
}) {
	const utils = trpc.useUtils();
	const invite = trpc.users.invite.useMutation({
		onSuccess: () => utils.users.listAdmin.invalidate(),
	});
	const [email, setEmail] = useState("");
	const [role, setRole] = useState<Role>("user");
	const [error, setError] = useState<string | null>(null);

	const dialogRef = useRef<HTMLDialogElement | null>(null);
	const emailInputRef = useRef<HTMLInputElement | null>(null);
	const titleId = useId();
	const descriptionId = useId();

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		if (!email.trim()) {
			setError("Email is required");
			return;
		}
		try {
			await invite.mutateAsync({ email: email.trim(), role });
			toast.success(`Invite sent to ${email.trim()}`);
			onClose();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to send invite");
		}
	}

	// Use native <dialog>.showModal(): browser handles ESC-to-close and focus
	// trap for free, plus the ::backdrop scrim replaces our manual overlay.
	useEffect(() => {
		const dlg = dialogRef.current;
		if (!dlg) return;
		if (!dlg.open) dlg.showModal();
		emailInputRef.current?.focus();
		function handleCancel(e: Event) {
			e.preventDefault();
			onClose();
		}
		dlg.addEventListener("cancel", handleCancel);
		return () => {
			dlg.removeEventListener("cancel", handleCancel);
			if (dlg.open) dlg.close();
		};
	}, [onClose]);

	// Quiet the unused currentUserId lint — kept for future "invited by me"
	// filters without touching the signature.
	void currentUserId;

	return (
		<dialog
			ref={dialogRef}
			aria-labelledby={titleId}
			aria-describedby={descriptionId}
			className="w-full max-w-sm rounded-lg border border-[#e5e7eb] bg-white p-6 shadow-xl backdrop:bg-black/40"
		>
			<div>
				<h3 id={titleId} className="text-base font-semibold text-[#1e1e20]">
					Invite a new user
				</h3>
				<p id={descriptionId} className="mt-1 text-xs text-[#6b7280]">
					They'll get an email with a link to set their password. The link expires in 7 days.
				</p>
				<form onSubmit={handleSubmit} className="mt-4 space-y-3">
					{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}
					<div className="space-y-1">
						<label htmlFor="invite-email" className="text-xs font-medium text-[#1e1e20]">
							Email
						</label>
						<input
							id="invite-email"
							ref={emailInputRef}
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
							placeholder="them@company.com"
						/>
					</div>
					<div className="space-y-1">
						<label htmlFor="invite-role" className="text-xs font-medium text-[#1e1e20]">
							Role
						</label>
						<select
							id="invite-role"
							value={role}
							onChange={(e) => setRole(e.target.value as Role)}
							className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						>
							<option value="user">user (can use the app)</option>
							<option value="admin">admin (can invite + configure)</option>
						</select>
					</div>
					<div className="flex items-center justify-end gap-2 pt-2">
						<button
							type="button"
							onClick={onClose}
							className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm font-medium text-[#6b7280] hover:bg-[#f8f9fb]"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={invite.isPending}
							className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
						>
							{invite.isPending ? "Sending…" : "Send invite"}
						</button>
					</div>
				</form>
			</div>
		</dialog>
	);
}
